/**
 * Region trait for geographic groupings of rooms (ADR-149).
 *
 * Entities with this trait represent named spatial regions. Rooms declare
 * membership via RoomTrait.regionId, and a room is in at most one region.
 * Regions do not nest (ADR-360 D5).
 *
 * Public interface: RegionTrait, IRegionData.
 * Owner context: @sharpee/world-model — traits / spatial
 */

import { ITrait } from '../trait.js';
import { TraitType } from '../trait-types.js';

/**
 * Data interface for RegionTrait construction.
 *
 * @param name - Human-readable region name (required).
 * @param ambientSound - Region-wide ambient sound propagated to rooms.
 * @param ambientSmell - Region-wide ambient smell propagated to rooms.
 * @param defaultDark - Whether rooms in this region default to dark.
 */
export interface IRegionData {
  name: string;
  ambientSound?: string;
  ambientSmell?: string;
  defaultDark?: boolean;
}

/**
 * Marks an entity as a spatial region that groups rooms.
 *
 * Rooms reference their region via `RoomTrait.regionId`. A region holds
 * rooms only, never another region, so a room's region is that one id.
 */
export class RegionTrait implements ITrait, IRegionData {
  static readonly type = TraitType.REGION;
  readonly type = TraitType.REGION;

  /** Human-readable region name. */
  name: string;

  /** Region-wide ambient sound propagated to contained rooms. */
  ambientSound?: string;

  /** Region-wide ambient smell propagated to contained rooms. */
  ambientSmell?: string;

  /** Whether rooms in this region default to dark. */
  defaultDark: boolean;

  constructor(data: IRegionData) {
    this.name = data.name;
    this.ambientSound = data.ambientSound;
    this.ambientSmell = data.ambientSmell;
    this.defaultDark = data.defaultDark ?? false;
  }
}
